import {defineStore} from "pinia";
import CheckpointSummary from "~/dto/checkpoint/Summary";
import TrackingSession from "~/dto/queue/TrackingSession";
import User from "~/dto/User";
import type SummaryResponse from "~~/api/response/SummaryResponse";
import type TrackingSessionData from "~~/api/response/data/queue/TrackingSessionData";
import type CheckpointSummaryData from "~~/api/response/data/checkpoint/SummaryData";
import type PublicSummaryData from "~~/api/response/data/summary/PublicSummaryData";
import type SummaryData from "~~/api/response/data/summary/SummaryData";
import type UserData from "~~/api/response/data/UserData";
import _ from "lodash";
import type CheckpointDetailsData from "~~/api/response/data/checkpoint/DetailsData";
import type QueueDetailsData from "~~/api/response/data/queue/QueueDetailsData";

export const useSummaryStore = defineStore('summary', () => {
  const { $apiFetch } = useNuxtApp();

  const checkpointsSummaryData = ref<Record<string, CheckpointSummaryData>>({});
  const checkpointsSummary = computed<CheckpointSummary[]>(() => {
    return _.map(checkpointsSummaryData.value, (data: CheckpointSummaryData) => new CheckpointSummary(data));
  });
  const openCheckpointsSummary = computed<CheckpointSummary[]>(() => {
    return _.filter(checkpointsSummary.value, (summary: CheckpointSummary) => summary.isOpen && !summary.hidden);
  });
  const isSummaryLoaded = computed<boolean>(() => {
    return checkpointsSummary.value.length > 0;
  });

  const userData = ref<UserData | null>(null);
  const user = computed<User | null>(() => {
    return userData.value ? new User(userData.value) : null;
  });

  const trackingSessionsData = ref<Record<string, TrackingSessionData>>({});
  const trackingSessions = computed<TrackingSession[]>(() => {
    return _.map(trackingSessionsData.value, (data: TrackingSessionData) => new TrackingSession(data));
  });

  const summaryPending = ref<boolean>(false);

  function setPublicSummaryData({checkpoints}: PublicSummaryData) {
    checkpointsSummaryData.value = _.keyBy(checkpoints || [], 'code');
  }

  function setPrivateSummaryData({user, tracking_sessions}: SummaryData) {
    userData.value = user;
    trackingSessionsData.value = _.keyBy(tracking_sessions || [], 'id');
  }

  function setSummaryData(data: SummaryData) {
    setPublicSummaryData(data);
    setPrivateSummaryData(data);
  }

  async function fetch() {
    if (_.size(checkpointsSummaryData.value)) {
      return checkpointsSummaryData.value;
    }

    summaryPending.value = true;
    try {
      const {data} = await $apiFetch<SummaryResponse>('/summary');
      setSummaryData(data);
    } catch (error) {
      captureException(error);
    }
    summaryPending.value = false;

    return checkpointsSummaryData.value;
  }

  function addTrackingSession(session: TrackingSession) {
    trackingSessionsData.value[session.id] = session.data;
  }

  function removeTrackingSession(sessionId: string) {
    delete trackingSessionsData.value[sessionId];
  }

  function hasTrackingSession(sessionId: string) {
    return _.has(trackingSessionsData.value, sessionId);
  }

  function updateTrackingSessionData(data: TrackingSessionData) {
    trackingSessionsData.value[data.id] = data;
  }

  function updateFromDetailsData(data: CheckpointDetailsData) {
    const summaryData = checkpointsSummaryData.value[data.checkpoint_code] ?
      toRaw(checkpointsSummaryData.value[data.checkpoint_code])
      : null;

    if (summaryData) {
      if (data.cars_queues?.live) {
        summaryData!.cars_queues = {
          live: buildQueueSummaryData(data.cars_queues.live),
          priority: data.cars_queues?.priority
            ? buildQueueSummaryData(data.cars_queues.priority)
            : null,
        };
      }

      if (data.motorcycles_queues?.live) {
        summaryData!.motorcycles_queues = {
          live: buildQueueSummaryData(data.motorcycles_queues.live),
          priority: data.motorcycles_queues?.priority
            ? buildQueueSummaryData(data.motorcycles_queues.priority)
            : null,
        };
      }
    }
  }

  function buildQueueSummaryData(data: QueueDetailsData) {
    return {
      count: data.count,
      duration: undefined !== data.hour_data?.duration ? data.hour_data.duration : null,
      throughput: undefined !== data.hour_data?.throughput ? data.hour_data.throughput : null,
    };
  }

  function getCheckpointData(code: string) {
    return _.find(checkpointsSummaryData.value, {code: code}) || null;
  }

  if (import.meta.client) {
    const {echoChannel} = useEcho();
    echoChannel("summary")
      .listen('.summary.public.update', function ({summary}: { summary: PublicSummaryData }) {
        setPublicSummaryData(summary);
      });
  }

  return {
    fetch,
    addTrackingSession,
    removeTrackingSession,
    hasTrackingSession,
    updateTrackingSessionData,
    updateFromDetailsData,
    getCheckpointData,
    isSummaryLoaded,
    checkpointsSummary,
    checkpointsSummaryData,
    summaryPending,
    openCheckpointsSummary,
    user,
    trackingSessions
  };
});
